// Email sections. Each block is plain data; render.ts turns a list of blocks into email HTML.

export interface Img {
  src: string; // absolute URL, or a path on this app like /stock/x.jpg or /img/abc
  alt: string;
  width: number;
  height: number;
}

export interface Tile {
  image: Img;
  label: string;
  url: string;
}

export interface TimelineRow {
  label: string;
  heading: string;
  text: string;
}

export interface PriceItem {
  name: string;
  was: string;
  price: string;
  unit: string;
}

export interface PriceGroup {
  name: string;
  items: PriceItem[];
}

export type Block =
  | { id: string; type: 'hero'; image: Img; headline: string; text: string; overlay: boolean }
  | { id: string; type: 'heading'; text: string; kicker: string; size: 'l' | 'm' }
  | { id: string; type: 'text'; html: string }
  | { id: string; type: 'image'; image: Img; link: string; caption: string }
  | { id: string; type: 'button'; label: string; url: string; note: string }
  | {
      id: string;
      type: 'split';
      image: Img;
      kicker: string;
      heading: string;
      html: string;
      linkLabel: string;
      url: string;
      imageSide: 'left' | 'right';
    }
  | { id: string; type: 'tiles'; items: Tile[] }
  | { id: string; type: 'timeline'; title: string; rows: TimelineRow[] }
  | { id: string; type: 'prices'; title: string; groups: PriceGroup[]; note: string }
  | { id: string; type: 'offer'; heading: string; text: string; label: string; url: string; style: 'dark' | 'card' }
  | { id: string; type: 'quote'; photo: Img | null; question: string; answer: string; name: string }
  | { id: string; type: 'divider' }
  | { id: string; type: 'spacer'; size: 's' | 'm' | 'l' };

export type BlockType = Block['type'];
export type BlockOf<T extends BlockType> = Extract<Block, { type: T }>;

export const BOOKING_URL = 'https://lavirmedspa.myaestheticrecord.com/online-booking';

export const JEN_PHOTO: Img = { src: '/brand/jen.jpg', alt: 'Jen Tancinco, RN', width: 400, height: 400 };

export function stock(name: string, alt: string, width = 1200, height = 800): Img {
  return { src: `/stock/${name}`, alt, width, height };
}

export function newId(): string {
  const a = new Uint8Array(6);
  crypto.getRandomValues(a);
  return 'b' + Array.from(a, (x) => x.toString(36).padStart(2, '0')).join('').slice(0, 9);
}

/** Palette entries, in the order the editor shows them. */
export const BLOCK_INFO: { type: BlockType; label: string; hint: string }[] = [
  { type: 'hero', label: 'Big photo', hint: 'A wide photo with a headline' },
  { type: 'heading', label: 'Headline', hint: '' },
  { type: 'text', label: 'Text', hint: '' },
  { type: 'image', label: 'Photo', hint: 'Full width' },
  { type: 'button', label: 'Button', hint: 'Book now, learn more' },
  { type: 'split', label: 'Photo + text', hint: 'Photo beside a short story' },
  { type: 'tiles', label: 'Two photos', hint: 'Side by side' },
  { type: 'timeline', label: 'Timeline', hint: 'When to book, steps, a schedule' },
  { type: 'prices', label: 'Price list', hint: '' },
  { type: 'offer', label: 'Offer box', hint: 'A special or a gift card deal' },
  { type: 'quote', label: 'Ask Jen', hint: "A question and Jen's answer" },
  { type: 'divider', label: 'Line', hint: '' },
  { type: 'spacer', label: 'Space', hint: '' },
];

export function createBlock(type: BlockType): Block {
  const id = newId();
  switch (type) {
    case 'hero':
      return { id, type, image: stock('u-glow-4-w.jpg', 'Woman with glowing skin'), headline: 'Your headline here', text: 'One or two sentences that say what this email is about.', overlay: false };
    case 'heading':
      return { id, type, text: 'Section title', kicker: '', size: 'm' };
    case 'text':
      return { id, type, html: 'Write something here. Keep it short: two or three sentences read best on a phone.' };
    case 'image':
      return { id, type, image: stock('u-spa-1-w.jpg', 'Treatment room at LaVir'), link: '', caption: '' };
    case 'button':
      return { id, type, label: 'Book now', url: BOOKING_URL, note: '' };
    case 'split':
      return { id, type, image: stock('u-serum-2-t.jpg', 'Skin care serum', 800, 1000), kicker: 'Treatment of the month', heading: 'Treatment name', html: 'What it does, how long it takes, and how long results last.', linkLabel: 'Learn more', url: 'https://www.lavirmedicalspa.com', imageSide: 'left' };
    case 'tiles':
      return {
        id,
        type,
        items: [
          { image: stock('u-gift-2-t.jpg', 'Gift card', 800, 1000), label: 'Gift cards', url: 'https://www.lavirmedicalspa.com' },
          { image: stock('u-facial-2-t.jpg', 'Facial', 800, 1000), label: 'Facials', url: 'https://www.lavirmedicalspa.com' },
        ],
      };
    case 'timeline':
      return {
        id,
        type,
        title: 'When to book',
        rows: [
          { label: '4 weeks out', heading: 'Dermal filler', text: 'Swelling settles in about two weeks.' },
          { label: '2 weeks out', heading: 'Botox or Jeuveau', text: 'Full results take about 14 days.' },
          { label: '2-3 days out', heading: 'HydraFacial', text: 'No downtime.' },
        ],
      };
    case 'prices':
      return {
        id,
        type,
        title: "This month's prices",
        groups: [
          { name: 'Injectables', items: [{ name: 'Botox', was: '$13', price: '$11', unit: '/unit' }, { name: 'Lip filler, 1 syringe', was: '', price: '$550', unit: '' }] },
          { name: 'Facials', items: [{ name: 'HydraFacial', was: '', price: '$175', unit: '' }] },
        ],
        note: '',
      };
    case 'offer':
      return { id, type, heading: 'Give a glow', text: 'Buy a $200 gift card and get a $25 bonus card for yourself.', label: 'Buy a gift card', url: 'https://www.lavirmedicalspa.com', style: 'dark' };
    case 'quote':
      return { id, type, photo: JEN_PHOTO, question: '"Will I look frozen?"', answer: 'No. I dose so your face still moves. You should look like you, just less tired.', name: 'Jen Tancinco, RN, BSN' };
    case 'divider':
      return { id, type };
    case 'spacer':
      return { id, type, size: 'm' };
  }
}

/** Deep copy with fresh ids (used for duplicate block and duplicate email). */
export function cloneBlock(b: Block): Block {
  return { ...structuredClone(b), id: newId() };
}

/** Every URL a block links to, in document order (used by the link checker). */
export function blockUrls(b: Block): string[] {
  switch (b.type) {
    case 'image':
      return b.link ? [b.link] : [];
    case 'button':
    case 'offer':
      return [b.url];
    case 'split':
      return b.linkLabel ? [b.url] : [];
    case 'tiles':
      return b.items.map((t) => t.url);
    default:
      return [];
  }
}

/** Every image in a block (for alt-text and size checks). */
export function blockImages(b: Block): Img[] {
  switch (b.type) {
    case 'hero':
    case 'image':
    case 'split':
      return [b.image];
    case 'tiles':
      return b.items.map((t) => t.image);
    case 'quote':
      return b.photo ? [b.photo] : [];
    default:
      return [];
  }
}
